import {useState} from 'react';
import {ArrowDown, ArrowUp} from 'lucide-react';
import {trpc} from '@common-web/lib/trpc';
import {cn} from '@common-web/lib/utils';
import {Card, CardContent} from '@common-web/components/ui/card';
import {Badge} from '@common-web/components/ui/badge';
import {Button} from '@common-web/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {
  DataTable,
  useDataTable,
  useResetPageOnChange,
  type Column,
} from '@common-web/components/data-table';
import type {RouterInput, RouterOutput} from '@common-web/lib/trpc';
import {
  ActiveFilterPills,
  ConsolidatedFilterBar,
  MultiSelectFilter,
  type MultiSelectOption,
} from '@internal/pages/claims/ConsolidatedFilterBar';
import {ClaimDetailSheet} from '@internal/pages/claims/ClaimDetailSheet';
import {useConsolidatedFilters} from '@internal/pages/claims/useConsolidatedFilters';

const PAGE_SIZE = 50;

type Row = RouterOutput['internal']['claimsTracking']['consolidated']['getClaims']['items'][number];
type SortBy = NonNullable<
  RouterInput['internal']['claimsTracking']['consolidated']['getClaims']['sortBy']
>;

// The API supports sort keys that have no column (stage, plan, received,
// service), so a "Sort by" dropdown complements the sortable column headers.
const SORT_OPTIONS: Array<{value: SortBy; label: string}> = [
  {value: 'group', label: 'Group'},
  {value: 'status', label: 'Status'},
  {value: 'stage', label: 'Stage'},
  {value: 'claimId', label: 'Claim ID'},
  {value: 'plan', label: 'Plan'},
  {value: 'received', label: 'Received date'},
  {value: 'service', label: 'Service date'},
  {value: 'openEx', label: 'Open ex codes'},
  {value: 'errFlags', label: 'ERR flags'},
];

const columns: Column<Row>[] = [
  {
    id: 'claimId',
    header: 'Claim ID',
    sortable: true,
    render: (row) => (
      <span className='font-mono text-sm text-brand-navy'>{row.claimId ?? '—'}</span>
    ),
  },
  {
    id: 'origin',
    header: 'Source',
    render: (row) => (
      <Badge variant='outline' className='font-mono text-xs'>
        {row.origin === 'claim' ? 'Claim' : 'Batch'}
      </Badge>
    ),
  },
  {
    id: 'group',
    header: 'Group',
    sortable: true,
    render: (row) => (
      <span className='text-sm max-w-[160px] truncate block' title={row.groupName ?? ''}>
        {row.groupName ?? row.groupId ?? '(No Group ID)'}
      </span>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    sortable: true,
    render: (row) => (
      <span className='text-sm' title={row.status ?? ''}>
        {row.statusDescription ?? row.status ?? '—'}
      </span>
    ),
  },
  {
    id: 'openEx',
    header: 'Open ex',
    sortable: true,
    align: 'right',
    render: (row) => {
      const total = row.openHeaderExCount + row.openLineExCount;
      return (
        <span className={cn('font-mono tabular-nums text-sm', total > 0 && 'text-orange-600')}>
          {total}
        </span>
      );
    },
  },
  {
    id: 'errFlags',
    header: 'ERR',
    sortable: true,
    align: 'right',
    render: (row) => (
      <span
        className={cn('font-mono tabular-nums text-sm', row.errFlagCount > 0 && 'text-red-600')}
      >
        {row.errFlagCount}
      </span>
    ),
  },
  {
    id: 'batch',
    header: 'Batch',
    render: (row) => (
      <span className='font-mono text-xs'>
        {row.batchNumber} / {row.batchClaim}
      </span>
    ),
  },
];

export function ClaimsTab() {
  const {filters, exCode, setExCode} = useConsolidatedFilters();

  const {data: exCodeData} = trpc.internal.claimsTracking.consolidated.getExCodeSummary.useQuery(
    {...filters, exCode: undefined},
    {throwOnError: false},
  );

  const exCodeOptions: MultiSelectOption[] =
    exCodeData?.map((o) => ({
      value: o.exCode,
      label: o.description ? `${o.exCode} — ${o.description}` : o.exCode,
    })) ?? [];

  // Local state (not URL) — the consolidated filters own the query string on
  // this dashboard, and page/sort were never deep-linkable here.
  const table = useDataTable({
    defaultPageSize: PAGE_SIZE,
    defaultSort: {key: 'group', direction: 'asc'},
    syncToUrl: false,
  });

  const [detail, setDetail] = useState<{batchNumber: number; batchClaim: number} | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Consolidated-filter changes live outside useDataTable, so reset the page.
  useResetPageOnChange(JSON.stringify(filters), table);

  const {data, isLoading, isFetching, isError, error} =
    trpc.internal.claimsTracking.consolidated.getClaims.useQuery({
      ...filters,
      page: table.page,
      pageSize: PAGE_SIZE,
      sortBy: (table.sort?.key ?? 'group') as SortBy,
      sortDir: table.sort?.direction ?? 'asc',
    });

  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  const openDetail = (row: Row) => {
    setDetail({batchNumber: row.batchNumber, batchClaim: row.batchClaim});
    setSheetOpen(true);
  };

  return (
    <div className='space-y-4'>
      <Card>
        <CardContent className='pt-6 space-y-3'>
          <div className='flex items-end gap-3 flex-wrap'>
            <ConsolidatedFilterBar />

            <div className='space-y-1.5'>
              <label className='block text-xs font-medium text-muted-foreground'>Ex code</label>
              <MultiSelectFilter
                placeholder='All ex codes'
                options={exCodeOptions}
                selected={exCode}
                onChange={setExCode}
                className='min-w-[220px]'
              />
            </div>

            <div className='space-y-1.5'>
              <label className='block text-xs font-medium text-muted-foreground'>Sort by</label>
              <div className='flex items-center gap-2'>
                <Select
                  value={table.sort?.key ?? 'group'}
                  onValueChange={(v) =>
                    table.setSort({key: v, direction: table.sort?.direction ?? 'asc'})
                  }
                >
                  <SelectTrigger className='w-[160px]'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SORT_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type='button'
                  variant='outline'
                  size='icon'
                  onClick={() =>
                    table.setSort({
                      key: table.sort?.key ?? 'group',
                      direction: (table.sort?.direction ?? 'asc') === 'asc' ? 'desc' : 'asc',
                    })
                  }
                  aria-label='Toggle sort direction'
                >
                  {(table.sort?.direction ?? 'asc') === 'asc' ? (
                    <ArrowUp className='h-3.5 w-3.5' />
                  ) : (
                    <ArrowDown className='h-3.5 w-3.5' />
                  )}
                </Button>
              </div>
            </div>
          </div>
          <ActiveFilterPills />
        </CardContent>
      </Card>

      <div className={isFetching && !isLoading ? 'opacity-70' : ''}>
        <DataTable<Row>
          table={table}
          columns={columns}
          data={data?.items}
          total={data?.total ?? 0}
          totalPages={totalPages}
          loading={isLoading}
          error={error}
          emptyMessage='No claims match these filters.'
          title='Claims'
          entity={{singular: 'claim', plural: 'claims'}}
          rowKey={(row) => `${row.batchNumber}-${row.batchClaim}`}
          onRowClick={openDetail}
        />
      </div>

      <ClaimDetailSheet
        batchNumber={detail?.batchNumber ?? null}
        batchClaim={detail?.batchClaim ?? null}
        open={sheetOpen}
        onOpenChange={(open) => {
          setSheetOpen(open);
          if (!open) setDetail(null);
        }}
      />
    </div>
  );
}
